import React, { useMemo } from 'react';
import QRCode from 'qrcode';

import './JoinQrCode.scss';

/**
 * Where a patron lands when they scan the venue screen: that player's jukebox page.
 * Built from the address the screen itself was opened on, so it points at whatever
 * host is serving the app rather than a hard-coded domain.
 */
export const jukeboxUrl = (origin: string, playerIdentifier: string) =>
  `${origin}/jukebox/${encodeURIComponent(playerIdentifier.toLowerCase())}`;

/** SVG path data for every dark module, one unit square each, offset by the quiet zone. */
export const qrPath = (text: string, quietZone: number) => {
  const { modules } = QRCode.create(text, { errorCorrectionLevel: 'M' });
  const { size } = modules;
  let d = '';
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      if (modules.get(row, col)) d += `M${col + quietZone} ${row + quietZone}h1v1h-1z`;
    }
  }
  return { d, size: size + quietZone * 2 };
};

interface JoinQrCodeProps {
  url: string;
  className?: string;
}

/**
 * The code drawn as an SVG, so it stays sharp at any screen size. Dark modules on a
 * light background with a quiet zone of two modules; phone cameras read that far more
 * reliably than an inverted or borderless code, especially from across a room.
 */
const JoinQrCode = ({ url, className }: JoinQrCodeProps) => {
  const { d, size } = useMemo(() => qrPath(url, 2), [url]);
  return (
    <svg
      className={`join-qr ${className || ''}`.trim()}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
      role="img"
      aria-label={`QR code for ${url}`}
    >
      <rect className="join-qr-light" width={size} height={size} />
      <path className="join-qr-dark" d={d} />
    </svg>
  );
};

export default JoinQrCode;
